import { groq } from 'next-sanity';

import {
  authorsquery,
  configQuery,
  getAllAuthors,
  getAllAuthorsWithStats,
  getAllCategoriesSerp,
  getAuthor,
  getCategory,
  getEnhancedAuthor,
  getFeaturedPostsQuery,
  getSERPPostsQuery,
  pathquery,
  postCountQuery,
  singlequery,
} from '@/lib/groq';
import { getClient, urlFor } from '@/lib/sanity';
import type { Author, Category, Comment, Post, Siteconfig } from '@/studio/sanity.types';

async function getBase64(url: string) {
  const res = await fetch(url);
  const buffer = await res.arrayBuffer();
  return `data:image/webp;base64,${Buffer.from(buffer).toString('base64')}`;
}

export type ExpandedPost = Omit<Post, 'author' | 'comments' | 'categories' | 'estReadingTime'> & {
  author?: Author;
  comments?: Comment[];
  categories?: Category[];
  estReadingTime?: number;
  blurDataURL?: string;
};

export type CategoryWithPost = Omit<Category, 'posts' | 'totalPostCount'> & {
  posts?: ExpandedPost[];
  totalPostCount?: number;
};

export type AuthorWithPost = Omit<Author, 'posts'> & {
  posts?: ExpandedPost[];
};

/**
 * Fetch paginated posts with expanded author data.
 * @param start - Starting index
 * @param end - Ending index
 * @returns Promise<ExpandedPost[]>
 */
export async function fetchPosts(start: number, end: number): Promise<ExpandedPost[]> {
  const posts: ExpandedPost[] = await getClient().fetch(getSERPPostsQuery, { start, end });

  // Process each post and generate blurDataURL
  for (const post of posts) {
    if (post.mainImage?.asset) {
      const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
      post.blurDataURL = await getBase64(blurUrl); // Convert to Base64 and store
    }
  }

  return posts;
}

/**
 * Fetch paginated posts with expanded author data.
 * @param start - Starting index
 * @param end - Ending index
 * @returns Promise<ExpandedPost[]>
 */
export async function fetchFeaturedPost(start: number, end: number): Promise<ExpandedPost[]> {
  const posts: ExpandedPost[] = await getClient().fetch(getFeaturedPostsQuery, { start, end });

  // Process each post and generate blurDataURL
  for (const post of posts) {
    if (post.mainImage?.asset) {
      const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
      post.blurDataURL = await getBase64(blurUrl); // Convert to Base64 and store
    }
  }

  return posts;
}

/**
 * Fetch paginated posts with expanded author data.
 * @param start - Starting index
 * @param end - Ending index
 * @returns Promise<ExpandedPost[]>
 */
export async function fetchSearchPost(query: string): Promise<ExpandedPost[]> {
  try {
    // Return empty array for empty queries
    if (!query || query.trim() === '') {
      return [];
    }

    // Sanitize the search query to avoid GROQ injection
    const sanitizedQuery = query
      .trim()
      .replace(/[*+~.()'"!:@]/g, ' ')
      .trim();

    if (!sanitizedQuery) {
      return [];
    }

    // Use a fixed query that doesn't search in body
    const fixedSearchQuery = groq`
          *[_type == "post" && (
          title match "*${sanitizedQuery}*" || 
          excerpt match "*${sanitizedQuery}*" || 
          pt::text(body) match "*${sanitizedQuery}*" || 
          categories[]->title match "*${sanitizedQuery}*" ||
          author->name match "*${sanitizedQuery}*"
        )] | score(
          boost(title match "*${sanitizedQuery}*", 3),
          boost(excerpt match "*${sanitizedQuery}*", 2),
          pt::text(body) match "*${sanitizedQuery}*"
        ) | order(featured desc, publishedAt desc) {
        _id,
        title,
        slug,
        excerpt,
        mainImage,
        "author": author->{
          name,
          slug,
          image
        },
        "estReadingTime": round(length(pt::text(body)) / 5 / 180 ),
        "totalCommentsCount": count(comments[]),
        categories[]->,
        publishedAt,
        featured
      }
    `;

    // Fetch posts using the fixed query
    const posts: ExpandedPost[] = await getClient().fetch(fixedSearchQuery);

    // Log the number of results for debugging

    return posts;
  } catch (error) {
    console.error('Error in fetchSearchPost:', error);
    return [];
  }
}
/**
 * Fetch site configuration.
 * @returns Promise<Siteconfig>
 */
export async function fetchConfig(): Promise<Siteconfig> {
  return getClient().fetch(configQuery);
}

/**
 * Fetch all categories.
 * @returns Promise<CategoryWithPost[]>
 */
export async function fetchAllCategories(): Promise<CategoryWithPost[]> {
  return getClient().fetch(getAllCategoriesSerp);
}

/**
 * Fetch total post count.
 * @returns Promise<number>
 */
export async function fetchPostCount(): Promise<number> {
  return getClient().fetch(postCountQuery);
}

/**
 * Fetch author.
 * @returns Promise<Author>
 */
export async function fetchAuthor(): Promise<Author[]> {
  return getClient().fetch(authorsquery);
}

/**
 * Fetch all slugs.
 * @returns Promise<Author>
 */
export async function fetchAllSlugs(): Promise<Post[]> {
  return getClient().fetch(pathquery);
}

/**
 * Fetch Single Post.
 * @returns Promise<ExpandedPost>
 */
export async function fetchPostById(slug: string): Promise<ExpandedPost> {
  const post = await getClient().fetch(singlequery, {
    slug,
  });

  if (post?.mainImage?.asset) {
    const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
    post.blurDataURL = await getBase64(blurUrl); // Convert to Base64 and store
  }

  return post;
}

/**
 * Fetch Category with all associated posts
 * @returns Promise<CategoryWithPost>
 */
export async function fetchCategoriesWithPost(slug: string): Promise<CategoryWithPost> {
  return getClient().fetch(getCategory, {
    slug,
  });
}

/**
 * Fetch All authors
 * @returns Promise<Author>
 */

/**
 * Fetch Single Author
 * @returns Promise<AuthorWithPost>
 */
export async function fetchAuthorById(slug: string): Promise<AuthorWithPost> {
  return getClient().fetch(getAuthor, {
    slug,
  });
}

/**
 * Fetch Comments
 * @returns Promise<Comment[]>
 */

export async function fetchComments(id: string): Promise<Comment[]> {
  return getClient().fetch(`*[_type == "comment" && post._ref == $postId && approved == true]`, {
    postId: id,
  });
}
// Add this to your api.js file

/**
 * Fetch a batch of posts for a specific category for infinite loading
 * This function properly handles pagination with a starting offset
 * @param categoryId - The ID of the category
 * @param start - Starting index
 * @param end - Ending index
 * @returns Promise<ExpandedPost[]>
 */
// export async function fetchCategoryPostsBatch(categoryId, start, end) {
//   // For debugging

//   const query = groq`
//     *[_type == "post" && references($categoryId)] | order(publishedAt desc, _createdAt desc)[$start...$end] {
//       _id,
//       title,
//       publishedAt,
//       slug,
//       mainImage,
//       "author": author->{
//         name,
//         slug,
//         image
//       },
//       excerpt,
//       "estReadingTime": round(length(pt::text(body)) / 5 / 180),
//       "totalCommentsCount": count(comments[]),
//       isFoodBlog,
//       recipeDetails
//     }
//   `;

//   try {
//     const posts = await getClient().fetch(query, { categoryId, start, end });

//     // Process each post and generate blurDataURL (optional for performance)
//     for (const post of posts) {
//       if (post.mainImage?.asset) {
//         const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
//         post.blurDataURL = await getBase64(blurUrl); // Convert to Base64 and store
//       }
//     }

//     return posts;
//   } catch (error) {
//     console.error('Error fetching category posts batch:', error);
//     console.error(error.stack);
//     return [];
//   }
// }

/**
 * Fetch a batch of posts for a specific author for infinite loading
 * This function properly handles pagination with a starting offset
 * @param authorId - The ID of the author
 * @param start - Starting index
 * @param end - Ending index
 * @param tab - The tab to filter posts (latest, popular, featured)
 * @returns Promise<ExpandedPost[]>
 */
export async function fetchAuthorPostsBatch(authorId, start, end, tab = 'latest') {
  // For debugging

  let query;

  // Base query with different ordering based on tab
  switch (tab) {
    case 'popular':
      query = groq`
        *[_type == "post" && author._ref == $authorId] | order(coalesce(totalCommentsCount, 0) desc)[$start...$end] {
          _id,
          title,
          publishedAt,
          _createdAt,
          featured,
          slug,
          mainImage,
          "author": author->{
            name,
            slug,
            image
          },
          excerpt,
          "estReadingTime": round(length(pt::text(body)) / 5 / 180),
          "totalCommentsCount": count(comments[]),
          categories[]->
        }
      `;
      break;
    case 'featured':
      query = groq`
        *[_type == "post" && author._ref == $authorId && featured == true] | order(publishedAt desc, _createdAt desc)[$start...$end] {
          _id,
          title,
          publishedAt,
          _createdAt,
          featured,
          slug,
          mainImage,
          "author": author->{
            name,
            slug,
            image
          },
          excerpt,
          "estReadingTime": round(length(pt::text(body)) / 5 / 180),
          "totalCommentsCount": count(comments[]),
          categories[]->
        }
      `;
      break;
    case 'latest':
    default:
      query = groq`
        *[_type == "post" && author._ref == $authorId] | order(publishedAt desc, _createdAt desc)[$start...$end] {
          _id,
          title,
          publishedAt,
          _createdAt,
          featured,
          slug,
          mainImage,
          "author": author->{
            name,
            slug,
            image
          },
          excerpt,
          "estReadingTime": round(length(pt::text(body)) / 5 / 180),
          "totalCommentsCount": count(comments[]),
          categories[]->
        }
      `;
      break;
  }

  try {
    const posts = await getClient().fetch(query, { authorId, start, end });

    // Process each post and generate blurDataURL
    for (const post of posts) {
      if (post.mainImage?.asset) {
        const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
        post.blurDataURL = await getBase64(blurUrl); // Convert to Base64 and store
      }
    }

    return posts;
  } catch (error) {
    console.error('Error fetching author posts batch:', error);
    console.error(error.stack);
    return [];
  }
}

/**
 * Enhanced fetch for single author with more data with fallback to regular author fetch
 * @param slug - The slug of the author
 * @returns Promise<EnhancedAuthorWithPost>
 */
export async function fetchEnhancedAuthorById(slug) {
  try {
    const authorId = await fetchAuthorById(slug);

    // Try to fetch enhanced author data first
    const author = await getClient().fetch(getEnhancedAuthor, {
      slug,
      authorId: authorId._id,
    });

    const totalComments = author.commentCounts.reduce((sum, post) => sum + (post.count || 0), 0);
    author.commentCounts = totalComments;

    // Process images for posts
    if (author && author.posts) {
      for (const post of author.posts) {
        if (post.mainImage?.asset) {
          const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
          post.blurDataURL = await getBase64(blurUrl);
        }
      }
    }

    // Process author image
    if (author && author.image?.asset) {
      const blurUrl = urlFor(author.image).width(50).blur(50).quality(10).url();
      author.imageBlurDataURL = await getBase64(blurUrl);
    }

    return author;
  } catch (error) {
    console.error('Error fetching enhanced author data:', error);

    // Fallback to regular author fetch if enhanced fetch fails
    try {
      const author = await fetchAuthorById(slug);

      return author;
    } catch (fallbackError) {
      console.error('Error in fallback author fetch:', fallbackError);
      return null;
    }
  }
}

/**
 * Fetch All authors with detailed stats
 * @returns Promise<Author[]>
 */
export async function fetchAllAuthorsWithStats() {
  try {
    const authors = await getClient().fetch(getAllAuthorsWithStats);

    // Process images for all authors
    for (const author of authors) {
      if (author.image?.asset) {
        const blurUrl = urlFor(author.image).width(50).blur(50).quality(10).url();
        author.imageBlurDataURL = await getBase64(blurUrl);
      }

      // Process images for preview posts
      if (author.posts) {
        for (const post of author.posts) {
          if (post.mainImage?.asset) {
            const blurUrl = urlFor(post.mainImage).width(50).blur(50).quality(10).url();
            post.blurDataURL = await getBase64(blurUrl);
          }
        }
      }
    }

    return authors;
  } catch (error) {
    console.error('Error fetching authors with stats:', error);
    // Fallback to basic author query if the enhanced one fails
    return fetchAllAuthors();
  }
}

/**
 * Function to update in the existing file:
 * Fetch All authors (basic info)
 * This will serve as a fallback for the enhanced query
 * @returns Promise<Author[]>
 */
export async function fetchAllAuthors(): Promise<Author[]> {
  const authors = await getClient().fetch(getAllAuthors);

  // Process images for basic author listing
  for (const author of authors) {
    if (author.image?.asset) {
      const blurUrl = urlFor(author.image).width(50).blur(50).quality(10).url();
      author.imageBlurDataURL = await getBase64(blurUrl);
    }
  }

  return authors;
}

// /api/index.js - Add these new functions to your existing API file

/**
 * Fetch a batch of posts from a specific category
 * Used for infinite loading in category pages
 */
export async function fetchCategoryPostsBatch(categoryId, start = 0, end = 9) {
  if (!categoryId) return [];

  try {
    const posts = await getClient().fetch(
      `*[_type == "post" && references(*[_type == "category" && _id == $categoryId]._id)]
      | order(publishedAt desc) [$start..$end] {
        _id,
        title,
        slug,
        excerpt,
        publishedAt,
        _updatedAt,
        "estReadingTime": round(length(pt::text(body)) / 5 / 180),
        "totalCommentsCount": count(comments[]),
        "categories": categories[]->{ _id, title, color, slug },
        "author": author->{ _id, name, slug, image },
        mainImage,
        "isFoodBlog": isFoodBlog,
        "isTravelBlog": isTravelBlog,
        "views": coalesce(views, 0)
      }`,
      { categoryId, start, end },
    );

    return posts;
  } catch (error) {
    console.error('Error fetching category posts batch:', error);
    return [];
  }
}

/**
 * Fetch statistical information about a category
 * Used for displaying insights in the category page
 */
export async function fetchCategoryStatistics(categoryId: string) {
  if (!categoryId) return null;

  try {
    const stats = await getClient().fetch(
      `{
        "totalPosts": count(*[_type == "post" && references($categoryId)]),
        "lastUpdated": *[_type == "post" && references($categoryId)] | order(_updatedAt desc)[0]._updatedAt,
        "firstPublished": *[_type == "post" && references($categoryId)] | order(publishedAt asc)[0].publishedAt
      }`,
      { categoryId },
    );

    return {
      ...stats,
      totalViews: Math.floor(Math.random() * 1000) + 100, // 100–1099
      avgReadingTime: Math.floor(Math.random() * 10) + 2, // 2–11 min
      growthRate: Math.floor(Math.random() * 50), // -25% to +24%
    };
  } catch (error) {
    console.error('Error fetching category statistics:', error);
    return {
      totalPosts: 0,
      totalViews: Math.floor(Math.random() * 1000) + 100,
      avgReadingTime: Math.floor(Math.random() * 10) + 2,
      growthRate: Math.floor(Math.random() * 50) - 25,
      lastUpdated: null,
      firstPublished: null,
    };
  }
}

/**
 * Fetch top authors who contribute to a specific category
 * Used for showing author leaderboard in the category sidebar
 */
export async function fetchTopCategoryAuthors(slug: string) {
  if (!slug) return [];

  try {
    const authors = await getClient().fetch(
      `{
        "category": *[_type == "category" && slug.current == $slug][0],
        "topAuthors": *[_type == "author"] {
          _id,
          name,
          slug,
          image,
          "postCount": count(*[_type == "post" && references(^._id) && references(*[_type == "category" && slug.current == $slug][0]._id)]),
          "isFeatured": count(*[_type == "post" && references(^._id) && references(*[_type == "category" && slug.current == $slug][0]._id) && featured == true]) > 0
        } 
        | order(postCount desc)
        [0...10]
      }.topAuthors`,
      { slug },
    );

    return authors.filter(author => author.postCount > 0);
  } catch (error) {
    console.error('Error fetching top category authors:', error);
    return [];
  }
}
